import {
  BrainCircuit,
  CalendarClock,
  ChevronRight,
  Gauge,
  ShieldAlert,
  Sparkles,
  Target,
  Users,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useData } from './DataContext';
import { useI18n } from './I18nContext';
import { TEAM_TOPICS, trainingForWeakness } from './trainingLibrary';

type DevelopmentSignal = {
  playerId: string;
  weakness: string;
  domain: 'technical' | 'tactical' | 'physical' | 'mental';
  severity: number;
  note: string;
  matchId: string;
  updatedAt: string;
};

type TrainingDay = {
  label: string;
  emphasis: string;
  intensity: 'Low' | 'Moderate' | 'High' | 'Match';
  content: string[];
};

function signalKey(teamId: string, playerId: string) {
  return `ecc_development_signal_${teamId}_${playerId}`;
}

function readSignals(teamId: string, playerId: string): DevelopmentSignal[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(signalKey(teamId, playerId)) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function dayDiff(from: Date, to: Date) {
  const ms = 24 * 60 * 60 * 1000;
  return Math.max(0, Math.round((to.getTime() - from.getTime()) / ms));
}

function smartWeek(daysToMatch: number): TrainingDay[] {
  const compact = daysToMatch <= 4;
  if (compact) {
    return [
      { label: 'MD-3/4', emphasis: 'Development + intensity', intensity: 'High', content: ['Individual weakness blocks', 'Small-sided transition game', 'Acceleration / speed exposure'] },
      { label: 'MD-2', emphasis: 'Tactical + set pieces', intensity: 'Moderate', content: ['Team tactical topic', 'Attacking / defending set pieces', 'Position-specific units'] },
      { label: 'MD-1', emphasis: 'Activation', intensity: 'Low', content: ['Technical activation', 'Rondos', 'Short tactical rehearsal', 'Finishing'] },
      { label: 'MD', emphasis: 'Matchday', intensity: 'Match', content: ['Warm-up', 'Match', 'Immediate recovery routine'] },
    ];
  }

  return [
    { label: 'MD+1', emphasis: 'Recovery / reset', intensity: 'Low', content: ['Low-load recovery', 'Mobility', 'Short technical work for non-starters'] },
    { label: 'MD-5', emphasis: 'Individual development', intensity: 'Moderate', content: ['Priority player development blocks', 'Technical activation', 'Strength / robustness'] },
    { label: 'MD-4', emphasis: 'High-intensity football', intensity: 'High', content: ['Small-sided games', 'Pressing / transition', 'Acceleration and COD'] },
    { label: 'MD-3', emphasis: 'Team tactical', intensity: 'Moderate', content: ['Build-up / defensive block', 'Unit work', 'Position-specific game phases'] },
    { label: 'MD-2', emphasis: 'Set pieces + final third', intensity: 'Moderate', content: ['Corners', 'Free kicks', 'Finishing', 'Opponent-specific patterns'] },
    { label: 'MD-1', emphasis: 'Activation / clarity', intensity: 'Low', content: ['Rondos', 'Passing', 'Short speed exposures', 'Tactical walkthrough'] },
    { label: 'MD', emphasis: 'Matchday', intensity: 'Match', content: ['Warm-up', 'Match', 'Immediate recovery routine'] },
  ];
}

export function CoachIntelligence() {
  const { t } = useI18n();
  const { team, players, matches, loading, error } = useData();
  const [teamTopicId, setTeamTopicId] = useState(TEAM_TOPICS[0].id);

  const futureMatches = useMemo(
    () =>
      [...matches]
        .filter((match) => new Date(match.match_date).getTime() >= Date.now() - 86400000)
        .sort((a, b) => a.match_date.localeCompare(b.match_date)),
    [matches],
  );

  const nextMatch = futureMatches[0] || null;
  const daysToMatch = nextMatch ? dayDiff(new Date(), new Date(nextMatch.match_date)) : 7;
  const week = smartWeek(daysToMatch);

  const playerNeeds = useMemo(() => {
    if (!team) return [];
    return players
      .flatMap((player) =>
        readSignals(team.id, player.id).map((signal) => ({
          player,
          signal,
          method: trainingForWeakness(signal.weakness),
        })),
      )
      .sort((a, b) => b.signal.severity - a.signal.severity)
      .slice(0, 12);
  }, [players, team]);

  const teamTopic = TEAM_TOPICS.find((item) => item.id === teamTopicId) || TEAM_TOPICS[0];

  const domainTotals = useMemo(() => {
    const totals = { technical: 0, tactical: 0, physical: 0, mental: 0 };
    playerNeeds.forEach((item) => { totals[item.signal.domain] += item.signal.severity; });
    return totals;
  }, [playerNeeds]);

  const topDomain = Object.entries(domainTotals).sort((a, b) => b[1] - a[1])[0]?.[0] || 'technical';

  if (loading) return <section className="metal-card loading-panel">{t.loading}…</section>;
  if (error) return <section className="metal-card error-panel">{error}</section>;

  return (
    <div className="coach-intelligence-page">
      <section className="metal-card coach-intelligence-hero">
        <div>
          <p className="eyebrow">COACH INTELLIGENCE</p>
          <h1>{t.coachIntelligence}</h1>
          <p className="muted">{t.coachIntelligenceSubtitle}</p>
        </div>
        <BrainCircuit size={34} />
      </section>

      <section className="coach-intelligence-grid">
        <article className="metal-card intelligence-priority-card">
          <div className="section-title-line">
            <div>
              <p className="eyebrow">{t.playerDevelopment}</p>
              <h2>{t.priorityNeeds}</h2>
            </div>
            <Target size={23} />
          </div>

          <div className="priority-needs-list">
            {playerNeeds.map(({ player, signal, method }) => (
              <div key={`${player.id}-${signal.weakness}`}>
                <span className={`priority-severity s${signal.severity}`}>{signal.severity}/5</span>
                <div>
                  <strong>#{player.squad_number ?? '—'} {player.name}</strong>
                  <small>{method.label} · {t[signal.domain]}</small>
                </div>
                <ChevronRight size={16} />
              </div>
            ))}
            {!playerNeeds.length && <p className="muted">{t.noDevelopmentNeeds}</p>}
          </div>
        </article>

        <article className="metal-card intelligence-team-card">
          <div className="section-title-line">
            <div>
              <p className="eyebrow">{t.teamWeakness}</p>
              <h2>{t.teamRecommendation}</h2>
            </div>
            <Users size={23} />
          </div>

          <div className="intelligence-domain-summary">
            {Object.entries(domainTotals).map(([domain, total]) => (
              <div key={domain} className={domain === topDomain ? 'dominant' : ''}>
                <span>{t[domain as 'technical' | 'tactical' | 'physical' | 'mental']}</span>
                <strong>{total}</strong>
              </div>
            ))}
          </div>

          <label className="team-recommendation-selector">
            <span>{t.teamTraining}</span>
            <select value={teamTopicId} onChange={(event) => setTeamTopicId(event.target.value)}>
              {TEAM_TOPICS.map((topic) => <option key={topic.id} value={topic.id}>{topic.label}</option>)}
            </select>
          </label>

          <div className="team-recommendation-body">
            <strong>{teamTopic.label}</strong>
            <p>{teamTopic.objective}</p>
            <small>{teamTopic.sessionStructure.join(' → ')}</small>
          </div>
        </article>
      </section>

      <section className="metal-card smart-training-planner">
        <div className="section-title-line">
          <div>
            <p className="eyebrow">{t.smartTrainingCalendar}</p>
            <h2>{nextMatch ? `${t.nextMatch}: ${nextMatch.opponent}` : t.trainingWeek}</h2>
          </div>
          <CalendarClock size={25} />
        </div>

        <div className="training-week-grid">
          {week.map((day) => (
            <article key={day.label} className={`training-day-card ${day.intensity.toLowerCase()}`}>
              <header>
                <strong>{day.label}</strong>
                <span>{day.intensity}</span>
              </header>
              <h3>{day.emphasis}</h3>
              <ul>
                {day.content.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </article>
          ))}
        </div>

        <p className="planner-note">
          <Gauge size={16} />
          {t.smartPlannerNote}
        </p>
      </section>

      <section className="metal-card intelligence-method-note">
        <Sparkles size={22} />
        <div>
          <strong>{t.intelligenceMethod}</strong>
          <span>{t.intelligenceMethodNote}</span>
        </div>
      </section>
    </div>
  );
}
